import {FC, useEffect, useState} from 'react'
import {dispatch} from 'app/store'
import classNames from 'classnames'
import {isEqual} from 'lodash'
import {useSelector} from 'react-redux'
import {useTranslation} from 'react-i18next'
import {useMutation} from '@tanstack/react-query'

import {LoadingSpinner, RenderIf} from 'components/Basic'
import {ActionContainer} from 'components/Layouts'
import {colorCodes, trailingSlashRegEx} from 'definitions/constant'
import {selectSettingsUsers, settingsActions} from 'features/Settings'
import {useActions, usePolicy, useUsersImported} from 'hooks'
import {SettingsService} from 'services'
import Toast from 'components/Basic/Toast'
import {
  DefaultBlockPagePayload,
  SliceStatus,
  UpdateCustomBlockPages,
  UpdateEndpointsConfigAction,
  SettingsRoutesEnum,
} from 'interfaces'
import {userActions} from '../../features/User/userSlice'
import {Button} from 'design'
import {
  getEndpointSettingsQueryCache,
  setEndpointsSettingCache,
  useEndpointSettingsQuery,
} from 'features/Settings/Endpoints/hooks/useEndpointSettingsQuery'
import {useEndpointConfigSnapshot} from 'hooks/snapshots/useEndpointConfigSnapshot'
import {CanIAccess, PERMISSIONS} from 'rbac'
import {
  clearEndpointSettingsSaveFailed,
  finishEndpointSettingsSave,
  isLatestEndpointSettingsSave,
  setEndpointSettingsSaveFailed,
  startEndpointSettingsSave,
  useEndpointSettingsSaveFailed,
} from 'features/Settings/Endpoints/hooks/useEndpointSettingsSaveFailed'

export const SettingsFooter: FC = () => {
  const {
    updateDefaultBlockPagesStatus,
    blockPages,
    endpointsConfig,
    defaultBlockPagePayload,
    status,
    updateCustomBlockPageStatus,
    savePressed,
    formSubmitted,
    updatedSettingField,
  } = useSelector(selectSettingsUsers)
  const {lastUpdated} = useUsersImported()

  const {pathname} = usePolicy()

  const endpointsEnabledRoutes = [
    SettingsRoutesEnum.ENDPOINTS,
    SettingsRoutesEnum.USERS,
    SettingsRoutesEnum.BLOCK_PAGES,
  ]

  const isEndpointsSettingsEnabled = endpointsEnabledRoutes.includes(
    pathname.replace(trailingSlashRegEx, '') as SettingsRoutesEnum,
  )

  const {endpointsConfigData} = useEndpointSettingsQuery(
    isEndpointsSettingsEnabled,
  )
  const {
    endpointConfigSnapshot,
    setEndpointConfigSnapshot,
    resetToEndpointConfigSnapshot,
  } = useEndpointConfigSnapshot()
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const {isSnackbarActionAvailable} = useActions()
  const {t} = useTranslation(['common', 'settings'])

  const pendingState =
    updateDefaultBlockPagesStatus === SliceStatus.pending ||
    status === SliceStatus.pending ||
    updateCustomBlockPageStatus === SliceStatus.pending ||
    isSaving

  const isEndpointConfigFormValid = endpointsConfig?.isFormValid
  const isSettingsUsersPage = pathname.includes(SettingsRoutesEnum.USERS)
  const hasEndpointSaveFailed = useEndpointSettingsSaveFailed()
  const showSaveFailed =
    hasEndpointSaveFailed &&
    (isSettingsUsersPage || pathname.includes(SettingsRoutesEnum.ENDPOINTS))

  const onCancel = () => {
    switch (pathname.replace(trailingSlashRegEx, '')) {
      case SettingsRoutesEnum.ENDPOINTS: {
        resetToEndpointConfigSnapshot()
        setEndpointSettingsSaveFailed(false)
        break
      }
      case SettingsRoutesEnum.BLOCK_PAGES:
        dispatch(settingsActions.removeBlockPagesSnapshot())
        dispatch(
          settingsActions.updateDefaultBlockPagePayload(
            {} as DefaultBlockPagePayload,
          ),
        )
        break
      case SettingsRoutesEnum.USERS: {
        resetToEndpointConfigSnapshot()
        setEndpointSettingsSaveFailed(false)
        dispatch(settingsActions.setShowEndpointConfigView(false))
        break
      }
    }
  }

  const saveChanges = () => {
    if (pathname.includes(SettingsRoutesEnum.BLOCK_PAGES)) {
      dispatch(settingsActions.formSubmitted(false))
      dispatch(settingsActions.savePressed(false))
      if (defaultBlockPagePayload.defaultPages?.length) {
        dispatch(
          settingsActions.defaultBlockPagesUpdate(defaultBlockPagePayload),
        )
      } else if (
        !isEqual(blockPages.data.pages.custom, blockPages.snapshot.pages.custom)
      ) {
        const updateCustomBlockPagePayload: UpdateCustomBlockPages = {
          addCustomPages: [],
          updateCustomPages: [],
        }
        blockPages.data.pages.custom.forEach(ele => {
          if (!ele.pageId) {
            updateCustomBlockPagePayload.addCustomPages?.push({
              label: ele.label,
              type: ele.type,
              html: ele?.html || '',
            })
          } else {
            const index = blockPages.updatedData.addCustomPages?.findIndex(
              item => item.label === ele.label,
            )
            if (index !== undefined && index >= 0) {
              const objToPush = {
                pageId: ele.pageId,
                type: ele.type,
                html: ele?.html || undefined,
                label: ele.label,
              }
              updateCustomBlockPagePayload.updateCustomPages?.push(objToPush)
            }
          }
        })
        if (
          blockPages.updatedData.deleteCustomPages &&
          blockPages.updatedData.deleteCustomPages?.length > 0
        ) {
          updateCustomBlockPagePayload.deleteCustomPages =
            blockPages.updatedData.deleteCustomPages
        }
        dispatch(
          settingsActions.customBlockPagesUpdate(updateCustomBlockPagePayload),
        )
        dispatch(settingsActions.resetSettingUpdateField())
      }
    }
  }

  // Resolves to whether the save went through. A failed save keeps the draft
  // and only the footer says so, so there's no success toast for it.
  const handleEndpointSettingsUpdate = async (
    payload: UpdateEndpointsConfigAction,
  ): Promise<boolean> => {
    const saveId = startEndpointSettingsSave()
    try {
      setIsSaving(true)
      const {status} = await SettingsService.updateEndpointsConfig(payload)
      if (status === 200 && endpointConfigSnapshot) {
        const savedConfig = {...endpointConfigSnapshot, ...payload}
        setEndpointConfigSnapshot(savedConfig)
        // If the page was left while saving (that drops the draft), show what
        // the server now has, not the old values. An edit made while saving is
        // kept, even one back to the old values, so it can be saved next.
        const currentConfig = getEndpointSettingsQueryCache().data
        if (
          !isLatestEndpointSettingsSave(saveId) &&
          isEqual(currentConfig, endpointConfigSnapshot)
        ) {
          setEndpointsSettingCache(savedConfig)
        }
      }
      finishEndpointSettingsSave(saveId, false)
      return true
    } catch (_error) {
      finishEndpointSettingsSave(saveId, true)
      return false
    } finally {
      setIsSaving(false)
    }
  }

  // A failed save is about that draft; once nothing is unsaved (for example
  // the admin undid the change by hand), the next change starts clean.
  useEffect(() => {
    if (
      hasEndpointSaveFailed &&
      endpointConfigSnapshot &&
      isEqual(endpointsConfigData, endpointConfigSnapshot)
    ) {
      clearEndpointSettingsSaveFailed()
    }
  }, [hasEndpointSaveFailed, endpointsConfigData, endpointConfigSnapshot])

  const updateEndpointConfig = useMutation(handleEndpointSettingsUpdate, {
    onSuccess: (isSaved: boolean) => {
      if (!isSaved) return
      Toast({
        message: t('settings:ENDPOINT_SETTINGS_UPDATE_SUCCESS'),
        type: 'success',
      })
    },
  })

  const onSaveChanges = () => {
    if (pathname.includes(SettingsRoutesEnum.ENDPOINTS)) {
      const payload = {
        antiTamperPassword: endpointsConfigData.antiTamperPassword,
        failClosed: endpointsConfigData.failClosed,
      }
      updateEndpointConfig.mutate(payload)

      // This dispatch action is temporary and will be removed when import status is part of login api response
      dispatch(
        userActions.setUpdatedAt(
          endpointsConfigData.requireDelegatedAuth ? lastUpdated ?? '' : '',
        ),
      )
    } else if (isSettingsUsersPage) {
      const payload = {...endpointsConfigData}
      updateEndpointConfig.mutate(payload)
    } else if (pathname.includes(SettingsRoutesEnum.BLOCK_PAGES)) {
      if (updatedSettingField.value) {
        dispatch(settingsActions.savePressed(true))
        document.getElementById('submit-custom-block-page')?.click()
      } else saveChanges()
    }
  }

  useEffect(() => {
    if (formSubmitted && savePressed) saveChanges()
  }, [formSubmitted, savePressed])

  return (
    <CanIAccess reqPermissions={[PERMISSIONS.SETTINGS.WRITE]}>
      <ActionContainer
        className={classNames(
          'border-t-0.5 border-gray-400 flex justify-end items-center bg-dope-black settings-mobile-save-bar',
          {
            hidden: !isSnackbarActionAvailable,
          },
        )}
      >
        <RenderIf isTrue={!showSaveFailed}>
          <p
            className="text-white karla-bold text-sm mr-6"
            data-cy="setting-confirm-footer"
            dangerouslySetInnerHTML={{
              __html: t('common:SAVE_FOOTER_MESSAGE'),
            }}
          ></p>
        </RenderIf>
        <RenderIf isTrue={showSaveFailed}>
          <p
            className="text-red-100 karla-bold text-sm mr-6 settings-save-failed"
            data-cy="setting-save-failed-footer"
            role="alert"
          >
            {t('settings:ENDPOINT_SETTINGS_SAVE_FAILED')}
          </p>
        </RenderIf>
        <Button
          className="mr-3"
          size="md"
          borderRadius="rounded"
          colorScheme="red"
          onClick={onCancel}
          // Cancel during a save would reset the page while the server may
          // still accept the change, leaving the two out of step.
          disabled={pendingState}
          data-cy="setting-cancel-button"
        >
          {t('common:CANCEL')}
        </Button>

        <Button
          size="sm"
          className="mr-6"
          borderRadius="rounded"
          data-cy="save-button-settings"
          onClick={onSaveChanges}
          disabled={
            pendingState || (isSettingsUsersPage && !isEndpointConfigFormValid)
          }
        >
          <RenderIf isTrue={!pendingState}>{t('common:SAVE')}</RenderIf>
          <RenderIf isTrue={pendingState}>
            <LoadingSpinner
              type="TailSpin"
              color={colorCodes.dopeBlack}
              width={16}
              height={16}
            />
          </RenderIf>
        </Button>
      </ActionContainer>
    </CanIAccess>
  )
}
